// The artwork upload wizard, expressed as a URL.
//
// The wizard used to exist only as a local boolean on LayoutBase, flipped by
// an event the navbars emit. That works for the one person who is already on
// the site, and for nobody else: the site owner's WordPress pages have no way
// to say "open the upload form", because there was nothing to link to.
//
// So '/add' becomes the whole answer to "is the wizard open" -- not a local
// flag that a route also happens to set, which is two sources of truth that
// drift the first time someone presses the browser's Back button. Deriving the
// flag from the route instead means the deep link, the navbar button and Back
// all agree by construction.
//
// Pure on purpose, like labels.js and geolocate.js: what is decided here is
// a rule about paths and history, and neither half needs a router, a DOM, or
// a mounted component to be checked.

/** Where the wizard lives. Mirrored by a Rails route (config/routes.rb) --
 * without one, a cold visit or a refresh at '/add' is a 404 served by Rails
 * before the SPA ever boots, exactly as it would be for '/art/:id'. */
export const WIZARD_PATH = '/add'

/** Where a visitor with no history behind them lands when they close it.
 * The map, because it is the site: '/' is the only route that is worth
 * showing to someone who has not asked for anything in particular yet. */
export const WIZARD_EXIT_PATH = '/'

/**
 * Whether a route path means "the wizard is open".
 *
 * Called with `$route.path`, which vue-router keeps free of the query string
 * and the hash -- those live in `$route.query` and `$route.hash`. The query is
 * stripped here anyway because a link written into a newsletter or a WordPress
 * menu arrives as '/add?utm_source=...' far more often than not, and a caller
 * reaching for `fullPath` instead of `path` is one refactor away. The failure
 * that would cause is the quiet kind: the map renders, no error is logged, and
 * the wizard the link was for simply never appears.
 *
 * @param {string} path Typically `$route.path`.
 * @returns {boolean}
 */
export function isWizardRoute(path) {
  // Not a string covers the moment before the router has resolved a route,
  // where `$route` itself is undefined.
  if (typeof path !== 'string') return false

  const withoutQuery = path.split(/[?#]/)[0]

  // Exact match after dropping ONE trailing slash, rather than startsWith:
  // the route is declared without `strict`, so vue-router matches '/add/' and
  // reports the path with the slash intact, while a prefix test would also
  // open the wizard over a future '/added' or '/add-artist' -- a bug that
  // presents as the wizard appearing at random.
  const normalized =
    withoutQuery.length > 1 && withoutQuery.endsWith('/')
      ? withoutQuery.slice(0, -1)
      : withoutQuery

  return normalized === WIZARD_PATH
}

/**
 * Where closing the wizard should send the visitor.
 *
 * Two arrivals, two different right answers, and the difference is invisible
 * from '/add' itself:
 *
 * - Opened from the navbar mid-session. The entry underneath '/add' is the
 *   page they were reading -- the map, or a specific piece -- so `back()` is
 *   both correct and free: no request, no reload, and their scroll position
 *   and map viewport survive.
 *
 * - Arrived cold on the link from WordPress. There is no in-app entry
 *   underneath: `back()` walks out of the site entirely, to the WordPress page
 *   or to the blank tab the link was opened in. The button says "close the
 *   wizard" and it would close the site.
 *
 * `inAppNavigations` is what separates them: the count of route changes the
 * SPA has performed since it booted. It is zero for exactly the cold case,
 * because the initial route is resolved before anything is watching, so no
 * change is ever recorded for it.
 *
 * The cold exit REPLACES rather than pushes. Pushing would stack '/' on top of
 * '/add', so the browser's Back button would reopen the wizard the visitor
 * just dismissed -- a dismissal that undoes itself -- and Back would never
 * reach the page they actually came from. Replacing leaves Back meaning what
 * it meant before they clicked the link.
 *
 * @param {number} inAppNavigations Route changes since the SPA booted.
 * @returns {{action: 'back'} | {action: 'replace', path: string}}
 */
export function resolveWizardExit(inAppNavigations) {
  // Proof, not a default. '3', NaN, undefined and -1 are all things a counter
  // that went missing or was never wired up can produce, and none of them is
  // evidence that there is somewhere on this site to go back TO. The two
  // failure modes are not equal: an unnecessary trip to '/' costs a click,
  // a wrong back() costs the visitor.
  const hasHistory =
    typeof inAppNavigations === 'number' &&
    Number.isFinite(inAppNavigations) &&
    inAppNavigations > 0

  if (hasHistory) return { action: 'back' }

  return { action: 'replace', path: WIZARD_EXIT_PATH }
}

/**
 * Compares two `$route.query` objects by value.
 *
 * Deliberately not `JSON.stringify` on both sides: `{...query}` is free to
 * reorder keys, and a repeat-parameter query ('?tag=a&tag=b') arrives as an
 * array, so a string comparison would report a change where there is none.
 */
function sameQuery(a, b) {
  const aKeys = Object.keys(a || {})
  const bKeys = Object.keys(b || {})
  if (aKeys.length !== bKeys.length) return false

  return aKeys.every(key => {
    const left = (a || {})[key]
    const right = (b || {})[key]
    if (Array.isArray(left) || Array.isArray(right)) {
      if (!Array.isArray(left) || !Array.isArray(right)) return false
      return left.length === right.length && left.every((v, i) => v === right[i])
    }
    return left === right
  })
}

/**
 * Whether a route change is nothing but the wizard opening or closing over
 * the page already on screen.
 *
 * HomePage renders at both '/' and '/add' (router.js), so it survives that
 * transition as the same instance -- which is the point, since it is the map
 * the visitor is meant to come back to. But its `$route` watcher refetches the
 * first page of arts and recentres the map on the first result on ANY route
 * change (HomePage.vue:494). Pressing "Upload Art" would therefore cost an API
 * round-trip and visibly move the map behind the modal, and closing the wizard
 * would cost a second one.
 *
 * That watcher exists for the FILTERS, which live entirely in the query. So
 * this asks the only question that matters to it: did anything about the list
 * change? Exactly one side being the wizard, with an identical query on both,
 * means no.
 *
 * The query has to match for the answer to be honest. LayoutBase carries
 * `$route.query` across when it opens and closes the wizard precisely so that
 * it does, but a caller that pushed '/add' bare would drop the visitor's
 * filters, and that IS a change the list has to act on.
 *
 * @param {{path: string, query: object}} to Destination route.
 * @param {{path: string, query: object}} from Route being left.
 * @returns {boolean}
 */
export function isWizardToggle(to, from) {
  if (!to || !from) return false

  // Exactly one side, by inequality: '/add' to '/add' is either not a
  // transition at all or a real query change that kept the wizard up.
  if (isWizardRoute(to.path) === isWizardRoute(from.path)) return false

  return sameQuery(to.query, from.query)
}

export default isWizardRoute
